/* eslint-disable max-lines */

import { ComputedCell, ResponsiveHeatMap } from '@nivo/heatmap';
import { clearTree, requestTreeFiltersToogle } from 'actions';
import { allowedReadOnlyDimensions, heatMapColors, metricsLinksMap } from 'constants/dashboard';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import withWidget from 'decorators/widget';
import WidgetLoading from 'decorators/widget/WidgetLoading';
import React from 'react';
import { useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { getCustomerDomain, getUser } from 'selectors/auth';
import { getTree } from 'selectors/dashboard';
import { getNavParamsByResourceMemo, getNavWidgetFiltersMemo } from 'selectors/nav';
import { getPermissionsPolicy } from 'selectors/permissions';
import { useSelector } from 'store';
import { DefaultWidgetComponentProps, MetricsDataV2 } from 'types';
import logEvent from 'utils/analytics';
import { joinQueryFilters } from 'utils/dashboard';
import { getValuesFromDimensions, transformToString } from 'utils/parserTree';
import { isActionAuthorized } from 'utils/permissions';
import { useHistory } from 'utils/urls';

// FIXME: Change DefaultWidgetComponentProps to use new metrics type
// once the migration to the new structure is done
export const HeatMapWidget: React.FC<
  DefaultWidgetComponentProps & { widgetData: MetricsDataV2 } & RouteComponentProps<{
      [paramName: string]: string | undefined;
    }>
> = (props) => {
  // @ts-ignore
  const { widgetId, widgetData, queryData, nivoProps, isExpandedView } = props;

  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const policy = useSelector(getPermissionsPolicy);
  const customerDomain = useSelector(getCustomerDomain);
  const widgetFilters = useSelector((state) => getNavWidgetFiltersMemo(state, widgetId));
  const filters = useSelector((state) =>
    getNavParamsByResourceMemo(state, resourceQueryParamName.metrics)
  );
  const urlTree = useSelector(getTree);

  const processedData: {
    data: { id: string; data: { x: string; y: number; labelValue: string }[] }[];
  } = { data: [] };

  widgetData?.results?.[0]?.y_axis?.forEach((y) => {
    processedData.data?.push({
      // @ts-ignore
      id: y.label,
      // @ts-ignore
      data: widgetData?.results?.[0]?.x_axis?.map((x, index) => ({
        x: x.label,
        // @ts-ignore
        y: y.values[index],
        labelValue: x.value,
      })),
    });
  });

  if (widgetData == null || queryData == null || !processedData) {
    return <WidgetLoading />;
  }

  const handleClick = (
    cell: ComputedCell<{
      x: string;
      y: number;
      labelValue: string;
    }>
  ): void => {
    const [id, type] = widgetId.split('--');
    logEvent(`Dashboard-click-${id}-${type}`);

    // this widget only ever has a single metric config
    // so we pluck the first query config
    const query = queryData.queries[0];
    if (query == null) {
      return;
    }

    const info = metricsLinksMap[query.metric];
    if (info == null || !isActionAuthorized(info.action, user.roles, policy)) {
      return;
    }

    const series = widgetData.results[0];

    if (series == null) {
      return;
    }

    const dimensions: Record<string, string | string[]> = {};
    const infoDimension = info.dimensions;

    if (infoDimension != null) {
      Object.entries(infoDimension).forEach(([dimension, param]) => {
        const allowed = allowedReadOnlyDimensions[query.metric];

        // if allowedDimension is not defined or the dimension is not included,
        // just ignore it

        if (allowed == null || !allowed.includes(dimension)) {
          return;
        }

        let value: string | string[] = '';
        let infoParam;

        if (Array.isArray(query.group_by)) {
          infoParam = infoDimension[query.group_by[query.group_by.length - 1]];
        } else {
          infoParam = query.group_by && infoDimension[query.group_by];
        }

        // add the resource uuid whose dimension was used as group by
        if (query.group_by && infoParam === param && cell.data.labelValue) {
          value = cell.data.labelValue;
        }

        if (filters[dimension] != null) {
          const topValue = filters[dimension];
          value = topValue;
        }

        if (widgetFilters[dimension] != null) {
          const widgetValue = filters[dimension];
          value = widgetValue;
        }

        if (query.group_by === dimension) {
          value = cell.data.labelValue;
        }

        dimensions[`${info.resource}__${param}`] = value;
      });

      if (Array.isArray(query.group_by)) {
        const value1 = widgetData.results[0].x_axis.find(
          (i) => i.value === cell.data.labelValue
        )?.value;
        let param = infoDimension[query.group_by[1]];

        if (value1) {
          dimensions[`${info.resource}__${param}`] = value1.toString();
        }

        const value2 = widgetData.results[0].y_axis.find(
          // @ts-ignore
          (i) => i.label === cell.serieId
          // @ts-ignore
        )?.id;

        param = infoDimension[query.group_by[0]];

        if (value2) {
          dimensions[`${info.resource}__${param}`] = value2.toString();
        }
      }
    }

    // @ts-ignore
    const widgetFilterSearch = query.filters_search || '';

    const tree = getValuesFromDimensions(dimensions);

    let searchString = transformToString(tree);
    if (typeof widgetFilterSearch === 'string') {
      searchString = joinQueryFilters(searchString, widgetFilterSearch);
    }

    searchString = joinQueryFilters(searchString, transformToString(urlTree));

    dispatch(requestTreeFiltersToogle({ value: true }));
    dispatch(clearTree());

    history.pushLookup({
      customerDomain,
      routeName: info.route,
      queryParams: {
        [`${info.resource}__filters_search`]: searchString,
        [`${info.resource}__has_events`]: ['true', 'false'],
        ...dimensions,
      },
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { colors, ...nivoArgs } = nivoProps || {};

  const widgetColors = heatMapColors;

  const renderChart = (): JSX.Element => (
    <>
      <div className={`h-full chart-svg-overflow ${isExpandedView ? '' : ''}`}>
        <ResponsiveHeatMap
          data={processedData.data}
          margin={{ top: 10, right: 50, bottom: 60, left: 270 }}
          valueFormat=">-.2~s"
          axisTop={null}
          xOuterPadding={-0.2}
          xInnerPadding={0.1}
          axisLeft={{ tickSize: 0, tickPadding: 15 }}
          axisBottom={{
            tickRotation: -45,
            tickSize: 0,
            tickPadding: 15,
            truncateTickAt: 12,
          }}
          cellComponent="circle"
          sizeVariation={{ sizes: [0.1, 1] }}
          colors={{
            type: 'quantize',
            colors: widgetColors,
          }}
          enableGridX
          enableGridY
          emptyColor="#555555"
          enableLabels={false}
          debounceResize={500}
          activeOpacity={1}
          inactiveOpacity={1}
          onClick={handleClick}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...nivoArgs}
        />
      </div>
    </>
  );

  return (
    <div className={`flex flex-col h-full ${!isExpandedView && 'overflow-visible'}`}>
      {renderChart()}
    </div>
  );
};

export default withWidget({
  title: 'Events',
  subtitle: '',
  allowDownload: true,
  allowExpandedView: true,
  allowTableView: true,
  reRenderWhitGrouping: true,
})(HeatMapWidget as unknown as React.FC<DefaultWidgetComponentProps>);
// FIXME: Remove this cast when the migration to the new structure is done
